import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AnswerForm from '../../components/templates/AnswerForm';
import DocumentPreview from '../../components/templates/DocumentPreview';

// Mock template data
const mockTemplate = {
  contentHtml: `
    <h1>Demo Sözleşme</h1>
    <p>Bu belge <strong>{{Ad_Soyad}}</strong> tarafından oluşturulmuştur.</p>
    <p>Adres: {{Adres}}</p>
    <p>Telefon: {{Telefon}}</p>
    <p>Açıklama: {{Aciklama}}</p>
    <p>Tarih: {{Tarih}}</p>
  `,
  questionTree: [
    {
      _id: 'demo-1',
      key: 'Ad_Soyad',
      displayText: 'Ad Soyad',
      type: 'Text',
      required: true,
      defaultValue: '',
      description: 'Belgeyi oluşturan kişinin tam adını giriniz',
      order: 0,
      step: 0,
      isActive: true,
    },
    {
      _id: 'demo-2',
      key: 'Adres',
      displayText: 'Adres',
      type: 'TextArea',
      required: false,
      defaultValue: '',
      description: 'Tam adres bilgisini giriniz (sokak, mahalle, ilçe, il)',
      order: 1,
      step: 1,
      isActive: true,
    },
    {
      _id: 'demo-3',
      key: 'Telefon',
      displayText: 'Telefon',
      type: 'Number',
      required: false,
      defaultValue: '',
      description: 'İletişim telefon numarasını giriniz (örn: 5551234567)',
      order: 2,
      step: 1,
      isActive: true,
    },
    {
      _id: 'demo-4',
      key: 'Aciklama',
      displayText: 'Açıklama',
      type: 'TextArea',
      required: false,
      defaultValue: '',
      description: 'Belge ile ilgili ek açıklamaları buraya yazabilirsiniz',
      order: 3,
      step: 2,
      isActive: true,
    },
    {
      _id: 'demo-5',
      key: 'Tarih',
      displayText: 'Tarih',
      type: 'Date',
      required: true,
      defaultValue: '',
      description: 'Belgenin geçerli olacağı tarihi seçiniz',
      order: 4,
      step: 2,
      isActive: true,
    },
  ],
};

/**
 * PublicDemoPage - Public demo sayfası
 * 
 * Authenticate olmayan kullanıcılar için belge oluşturma sürecini gösterir.
 * Mock template ve sorular ile çalışır, backend'e istek yapmaz.
 * 
 * Features:
 * - Mock template data
 * - Local state ile answers yönetimi
 * - AnswerForm ve DocumentPreview component'leri
 * - Grid layout (sol: sorular, sağ: önizleme)
 */
const PublicDemoPage = () => {
  const history = useHistory();
  const [answers, setAnswers] = useState({});

  const handleAnswerChange = (newAnswers) => {
    setAnswers(newAnswers);
  };

  const handleQuestionsComplete = () => {
    // Demo'da tamamlama işlemi yok, sadece önizleme gösteriliyor
    // Kullanıcı soruları cevaplayıp önizlemeyi görebilir
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/')}
            sx={{ mb: 2 }}
          >
            Ana Sayfaya Dön
          </Button>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Demo: Belge Oluşturma
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bu demo sayfasında belge oluşturma sürecini deneyebilirsiniz. Soruları cevaplayın ve önizlemeyi gerçek zamanlı olarak görün.
          </Typography>
        </Box>

        {/* Main Content */}
        <Grid container spacing={1}>
          {/* Left: Answer Form */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '600px',
                maxHeight: '80vh',
                overflow: 'hidden',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Soruları Cevaplayın
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  mt: 2,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                    transition: 'background 0.2s ease',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.35)',
                    },
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                }}
              >
                <AnswerForm
                  questions={[]}
                  questionTree={mockTemplate.questionTree}
                  answers={answers}
                  onChange={handleAnswerChange}
                  onClear={() => handleAnswerChange({})}
                  onComplete={handleQuestionsComplete}
                  showAllQuestions={false}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Right: Document Preview */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '600px',
                maxHeight: '80vh',
                overflow: 'hidden',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Önizleme
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  mt: 2,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '3px',
                    transition: 'background 0.2s ease',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.35)',
                    },
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                }}
              >
                <DocumentPreview
                  content={mockTemplate.contentHtml}
                  answers={answers}
                  questionTree={mockTemplate.questionTree}
                  status="draft"
                  skipApiCalls={true}
                  showOptionalPlaceholders={true}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Footer Info */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Bu bir demo sayfasıdır. Gerçek belge oluşturmak için{' '}
            <Button
              variant="text"
              size="small"
              onClick={() => history.push('/register')}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
            >
              kayıt olun
            </Button>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicDemoPage;

